import { Link } from "react-router-dom";
import { ArrowRight, FileText, Zap, Download, Car, UserRound, ShieldCheck, Clock, Building2, CreditCard, Printer, Check, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { STATES } from "@/lib/constants";

const STEPS = [
  { icon: Zap, title: "Get your quote", text: "Tell us about you, your vehicle or your business. See your final price instantly in about two minutes." },
  { icon: CreditCard, title: "Pay online", text: "Pay monthly or in full with any debit or credit card right after you see your price." },
  { icon: Printer, title: "Download, print & go", text: "Your policy is issued online as soon as you pay. Download or print it right away. SR22 proof is ready as soon as the state accepts your filing." },
];

const PRODUCTS = [
  {
    icon: Car, key: "owner", title: "Personal Auto Insurance", price: "Liability limits from $10,000",
    points: ["Liability-only or full coverage", "Limits from $10,000 up to $100,000 / $300,000", "SR22 filing available"],
  },
  {
    icon: Building2, key: "commercial", title: "Commercial Auto Liability", price: "Up to $1,000,000 combined limit",
    points: ["Contractors, delivery, trucking & more", "Cover one vehicle or your whole fleet", "SR22 filing for business drivers"],
  },
  {
    icon: Briefcase, key: "general_liability", title: "General Liability Insurance", price: "Up to $2M / $4M",
    points: ["Bodily injury & property damage claims", "Personal & advertising injury", "Certificates for contracts and leases"],
  },
  {
    icon: UserRound, key: "non_owner", title: "Non-Owner & SR22 Insurance", price: "Fast state filing",
    points: ["For drivers who don't own a car", "SR22 and FR-44 filings in all 50 states", "Track your filing online"],
  },
];

const Products = () => (
  <section className="border-b border-slate-200 bg-white">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-24">
      <p className="eyebrow">Our policies</p>
      <h2 className="mt-3 max-w-2xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Auto, commercial auto and general liability insurance, all online.</h2>
      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {PRODUCTS.map((p) => (
          <div key={p.key} className="flex flex-col rounded-lg border border-slate-200 p-8 transition-colors duration-200 hover:border-blue-300" data-testid={`product-card-${p.key}`}>
            <p.icon className="h-7 w-7 text-blue-700" />
            <h3 className="mt-6 text-xl font-semibold">{p.title}</h3>
            <p className="mt-1 font-mono text-sm font-semibold text-emerald-700">{p.price}</p>
            <ul className="mt-5 flex-1 space-y-2 text-sm text-slate-600">
              {p.points.map((t) => <li key={t} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{t}</li>)}
            </ul>
            <Button asChild variant="outline" className="mt-8 hover:bg-blue-700 hover:text-white" data-testid={`product-quote-btn-${p.key}`}>
              <Link to={`/quote?type=${p.key}`}>Get a quote <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FAQ = [
  ["What policies do you offer?", "Personal auto insurance (liability-only or full coverage), non-owner auto insurance, commercial auto liability for business vehicles, and commercial general liability for businesses. SR22 and FR-44 filings are available with our auto policies."],
  ["What liability limits can I choose?", "Personal auto limits start at $10,000 / $20,000 / $10,000 and go up to $100,000 / $300,000 / $100,000. Commercial auto liability ranges from a $100,000 to a $1,000,000 combined single limit, and general liability from $300,000 / $600,000 up to $2,000,000 / $4,000,000 (per occurrence / aggregate). If your state requires more than the limit you pick, your policy automatically meets the state minimum."],
  ["How do I pay and get my policy?", "Your final price appears instantly when you finish your quote. Pay online by debit or credit card, either monthly or in full (save 10%). Your policy is issued right away, and you can download or print it from your account."],
  ["How do I file a claim or get help?", "Everything is handled online, 24/7. Log in to file a claim in a few minutes, upload photos or a police report, and track its status. Our AI assistant in your account can answer questions about your policy, payments, balance, filing status and claims instantly."],
  ["Do businesses get a bundle discount?", "Yes. Businesses that carry both commercial auto liability and general liability with us get 10% off the estimate on the second policy."],
  ["What is an SR22?", "An SR22 is a certificate your insurer files with the state proving you carry the required liability insurance. It's often required after a DUI, driving uninsured, serious violations or a license suspension."],
  ["How long do I need an SR22?", "Most states require an SR22 for about 3 years, but it varies by state and offense. If your policy lapses, we're required to notify the state (an SR26)."],
  ["What does the $25 filing fee cover?", "A one-time fee for preparing and electronically submitting your SR22 (or equivalent form) to your state. There's no filing fee if you don't need an SR22."],
];

const Hero = () => (
  <>
  <Link to="/quote" className="block bg-emerald-500 px-4 py-2.5 text-center text-sm font-bold text-slate-950 transition-colors hover:bg-emerald-400" data-testid="price-drop-banner">
    Now up to 20% lower on every policy. Get your instant price →
  </Link>
  <section className="relative overflow-hidden bg-slate-950 text-white">
    <img src="/images/hero.jpg" alt="Car on an open road" className="absolute inset-0 h-full w-full object-cover opacity-30" />
    <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
    <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 md:py-28 lg:grid-cols-12">
      <div className="fade-up lg:col-span-7">
        <p className="eyebrow !text-blue-300">Personal Auto · Commercial Auto · General Liability · SR22 Filings</p>
        <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
          Auto and commercial auto insurance, <span className="text-emerald-400">issued online.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base text-slate-300 md:text-lg">
          Request a quote in minutes, pay securely by debit or credit card, and download or print your policy as soon as it is issued. Auto, commercial auto and general liability coverage is available in all 50 states and DC, including SR22 and FR-44 filings.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild size="lg" className="bg-blue-600 text-white hover:bg-blue-500 hover:text-white" data-testid="hero-quote-btn">
            <Link to="/quote">Request a quote <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-slate-600 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="hero-login-btn">
            <Link to="/login">Pay or print my policy</Link>
          </Button>
        </div>
      </div>
      <div className="fade-up grid grid-cols-2 gap-3 self-end lg:col-span-5" style={{ animationDelay: ".15s" }}>
        <PriceCard icon={Car} label="Personal auto limits" range="From $10k" />
        <PriceCard icon={Building2} label="Commercial auto limit" range="Up to $1M" />
        <div className="col-span-2 rounded-lg border border-white/10 bg-white/5 p-4 text-sm text-slate-300 backdrop-blur-md">
          <Download className="mb-2 h-4 w-4 text-amber-400" /> Pay by debit or credit card, then download or print your policy online. No office visit needed.
        </div>
      </div>
    </div>
  </section>
  </>
);

const PriceCard = ({ icon: Icon, label, range, suffix }) => (
  <div className="rounded-lg border border-white/10 bg-white/5 p-5 backdrop-blur-md">
    <Icon className="h-5 w-5 text-blue-300" />
    <p className="mt-4 text-xs uppercase tracking-wider text-slate-400">{label}</p>
    <p className="mt-1 font-mono text-2xl font-semibold">{range} <span className="text-sm text-slate-400">{suffix}</span></p>
  </div>
);

const HowItWorks = () => (
  <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28">
    <p className="eyebrow">How it works</p>
    <h2 className="mt-3 max-w-2xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Quote, pay and print your policy without leaving home.</h2>
    <div className="mt-12 grid gap-4 md:grid-cols-3">
      {STEPS.map((s, i) => (
        <div key={s.title} className="group rounded-lg border border-slate-200 bg-white p-8 transition-colors duration-200 hover:border-blue-300" data-testid={`how-step-${i + 1}`}>
          <div className="flex items-center justify-between">
            <s.icon className="h-6 w-6 text-blue-700" />
            <span className="font-mono text-sm text-slate-400">0{i + 1}</span>
          </div>
          <h3 className="mt-8 text-xl font-semibold">{s.title}</h3>
          <p className="mt-2 text-slate-600">{s.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const StatesGrid = () => (
  <section className="border-y border-slate-200 bg-white">
    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <p className="eyebrow">Coverage</p>
        <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">Coverage in every state.</h2>
        <p className="mt-4 text-slate-600">Pick your state to start a quote for personal or commercial auto. We also handle SR22, FR-44 and the equivalent proof forms used in states without SR22.</p>
        <img src="/images/driver.jpg" alt="Driver holding car keys" className="mt-8 hidden h-56 w-full rounded-lg object-cover lg:block" />
      </div>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-9 lg:col-span-8 lg:grid-cols-8">
        {STATES.map((s) => (
          <Link
            key={s.code}
            to={`/quote?state=${s.code}`}
            title={s.name}
            data-testid={`state-tile-${s.code}`}
            className="flex h-14 items-center justify-center rounded-md border border-slate-200 font-mono text-sm font-semibold text-slate-700 transition-colors duration-150 hover:border-blue-700 hover:bg-blue-700 hover:text-white"
          >
            {s.code}
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const Faq = () => (
  <section className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 md:py-28 lg:grid-cols-12">
    <div className="lg:col-span-4">
      <p className="eyebrow">FAQ</p>
      <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">Your questions, answered.</h2>
      <div className="mt-8 flex items-center gap-3 rounded-lg bg-slate-900 p-5 text-white">
        <ShieldCheck className="h-8 w-8 shrink-0 text-emerald-400" />
        <p className="text-sm">Instant final prices, now up to 20% lower. Buy, pay and print in minutes.</p>
      </div>
    </div>
    <Accordion type="single" collapsible className="lg:col-span-8" data-testid="faq-accordion">
      {FAQ.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`}>
          <AccordionTrigger className="text-left text-base font-semibold" data-testid={`faq-item-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-slate-600">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

const Cta = () => (
  <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6">
    <div className="grid overflow-hidden rounded-xl bg-blue-700 text-white md:grid-cols-2">
      <div className="p-10 md:p-14">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Get covered today. Pay online and print your policy.</h2>
        <p className="mt-3 text-blue-100">Personal auto, commercial auto, general liability and SR22. No obligation, takes about two minutes.</p>
        <Button asChild size="lg" className="mt-8 bg-white text-blue-800 hover:bg-blue-50 hover:text-blue-900" data-testid="cta-quote-btn">
          <Link to="/quote">Get my quote <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>
      <img src="/images/agent.jpg" alt="Support agent with headset" className="hidden h-full w-full object-cover md:block" />
    </div>
  </section>
);

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Hero />
      <Products />
      <HowItWorks />
      <StatesGrid />
      <Faq />
      <Cta />
    </div>
  );
}
